import { FlowFormPanel } from "#batteries-included-components/Panels/FormPanels/FlowFormPanel/FlowFormPanel";
import { useCreateFlow, useListFlowTypes } from "#hooks/adapters/useFlows";
import { useNavigate, useParams } from "#routers/hooks";
import {
  AssetCreateMode,
  type AssetCreateModeType,
} from "#src/batteries-included-components";
import {
  TemplatedConfigurationQueueType,
  templatedConfigurationRunQueueName,
} from "#src/batteries-included-components/Banners/TemplatedConfigurationRunStatus";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import { useCreateOneTemplatedConfigurationRun } from "#src/components/hooks/adapters/useTemplatedConfigurations";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import {
  MultiStepFormProvider,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { useQueueUnique } from "#src/hooks/useQueue";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { sanitizeCustomAttributeFormInputs } from "#src/utils/customAttributes";
import { useBreadcrumbsFromRoute } from "#utils/route";
import { Page, Panel } from "@validereinc/common-components";
import {
  AssetType,
  Resources,
  type FlowCreateOneRequestType,
} from "@validereinc/domain";
import type { ExcludeUndefined } from "@validereinc/utilities";
import classNames from "classnames";
import { pick } from "es-toolkit/compat";
import React from "react";
import { FlowListRoute } from "../";
import { FlowDetailRoute } from "../[flowId]/detail";
import { FlowCreateRoute } from "./";
import styles from "./CreateFlowPage.module.scss";

const cx = classNames.bind(styles);

const CreateFlowPageContent = ({
  assetCreateMode,
  shouldUseAssetBuilder,
}: {
  assetCreateMode: AssetCreateModeType;
  shouldUseAssetBuilder: boolean;
}) => {
  const { localize, isLoading: isMappingLoading } = useLocalization();
  const { stepper, footer, currentStep } = useMultiStepFormContext();
  const [breadcrumbs] = useBreadcrumbsFromRoute(FlowCreateRoute);
  const [filterConfigStorageKey] = useStorageKey(
    ["filter-configuration"],
    ["asset-builder", localize(AssetType.FLOW)]
  );

  return (
    <Page
      breadcrumbs={breadcrumbs}
      title={`Create ${localize(AssetType.FLOW)}`}
      category={
        shouldUseAssetBuilder
          ? "Asset Builder"
          : `Quick Create ${localize(AssetType.FLOW)}`
      }
      shouldCenterContent
      shouldContentBeFullWidth={
        assetCreateMode === AssetCreateMode.QUICK_CREATE
      }
      footer={footer}
      isLoading={isMappingLoading}
    >
      <div className={cx("container")}>
        <Panel
          className={cx("panel")}
          isFluidY={false}
        >
          <div className={cx("stepper-container")}>{stepper}</div>
          <div
            className={cx("form-container", currentStep === 3 && "should-grow")}
          >
            <FlowFormPanel
              flowId=""
              filterConfigStorageKey={filterConfigStorageKey}
              assetCreateMode={assetCreateMode}
              shouldUseAssetBuilder={shouldUseAssetBuilder}
            />
          </div>
        </Panel>
      </div>
    </Page>
  );
};

export const CreateFlowPage = () => {
  const { mode } = useParams<{ mode: AssetCreateModeType }>();
  const navigate = useNavigate();
  const [areTemplatedConfigurationsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:templated_configurations",
  });

  const createFlow = useCreateFlow({
    onSuccess: (data) => {
      navigate(
        FlowDetailRoute.toLinkParts({ pathParams: { flowId: data?.data?.id } })
      );
    },
  });

  const customAttributes =
    useListCustomAttributes({ filters: { entity_type: AssetType.FLOW } }).data
      ?.data ?? [];

  const [queue, setQueue] = useQueueUnique<TemplatedConfigurationQueueType>(
    [],
    templatedConfigurationRunQueueName(Resources.FLOW),
    (tx) => tx.job.id
  );

  const flowTypesQuery = useListFlowTypes();
  const flowTypes = flowTypesQuery.data?.data ?? [];

  const createTemplatedConfigurationRun = useCreateOneTemplatedConfigurationRun(
    {
      onSuccess: (data, variables) => {
        const response = data.data;

        const primaryResourceId =
          Object.keys(variables?.data?.custom_attribute_configuration)?.[0] ??
          "";

        const flowTypeUsedForTemplatedRunCreation = primaryResourceId
          ? response?.custom_attribute_configuration?.[primaryResourceId]
              .entity_subtype
          : "";

        const flowTypeDefinitionUsedForTemplatedRunCreation = flowTypes.find(
          (flowType) => flowType.id === flowTypeUsedForTemplatedRunCreation
        );

        setQueue(
          queue.insert({
            job: {
              id: response.id,
              templateName: response.templated_configuration_name,
            },
            resourceDetails: {
              type: flowTypeDefinitionUsedForTemplatedRunCreation,
            },
          })
        );
        navigate({
          pathname: FlowListRoute.toLink(),
        });
      },
    }
  );

  const onCancel = () =>
    navigate({
      pathname: FlowListRoute.toLink(),
    });

  const onSubmit = (
    values: Array<
      Partial<FlowCreateOneRequestType["data"]> & { template_name?: string }
    >
  ) => {
    const allValues = values.reduce(
      (total, current) => ({ ...total, ...current }),
      {}
    );

    allValues.custom_attributes = sanitizeCustomAttributeFormInputs(
      allValues?.custom_attributes ?? {},
      customAttributes
    );

    if (allValues.template_name) {
      createTemplatedConfigurationRun.mutate({
        data: {
          ...pick(allValues, "inputs", "custom_attribute_inputs"),
          custom_attribute_configuration: {
            [Resources.FLOW]: {
              entity_subtype: allValues.type,
            },
          },
        },
        meta: {
          templated_configuration_name: allValues.template_name,
        },
      });
    } else {
      createFlow.mutate({
        data: allValues as ExcludeUndefined<FlowCreateOneRequestType["data"]>,
      });
    }
  };

  const shouldUseAssetBuilder =
    areTemplatedConfigurationsAvailable &&
    mode === AssetCreateMode.ASSET_BUILDER;

  return (
    <MultiStepFormProvider
      steps={[
        ...[
          {
            label: "Overview",
          },
          {
            label: "Details",
          },
        ],
        ...(!shouldUseAssetBuilder ? [{ label: "Connections" }] : []),
      ]}
      onCancel={onCancel}
      onSubmit={onSubmit}
      onSubmitMutationState={{
        isSubmitting: createFlow.isLoading,
      }}
      orientation="vertical"
      submissionActionText="Create"
      stepperClassName={cx("sticky-stepper")}
    >
      <CreateFlowPageContent
        assetCreateMode={mode}
        shouldUseAssetBuilder={shouldUseAssetBuilder}
      />
    </MultiStepFormProvider>
  );
};
